import clsx from "clsx";
import { memo, useEffect, useMemo, useState } from "react";

import { CapacityStatus, ServiceReserveStatus } from "@/apis/clients";
import BudgeOptional from "@/components/atoms/inputForms/BudgeOptional";
import { Label } from "@/components/atoms/inputForms/Label";
import EditorFrame from "@/components/editors/EditorFrame";
import { ScheduleViewItems } from "@/components/editors/template-blocks/service-menu/service-menu-items/ScheduleViewItems";
import { CheckboxGroup } from "@/components/molecules/forms/CheckboxGroup";
import { ServiceOptionDescribe } from "@/components/organisms/service-form/ServiceOptionDescribe";
import { useSubmitForm } from "@/hooks/useSubmitForm";
import { Schedule } from "@/states/page-editor/template-parts/service";
import { ServiceMenuTemplate as IServiceMenuTemplate } from "@/states/page-editor/template-parts/service-menu";
import { useHandleFieldValueChange } from "@/states/service-form/operations";

import {
  useFormData,
  useServiceFormUiState,
  useServiceInfo,
} from "@/states/service-form/selectors";
import {
  FieldValueChangeHandler,
  ServiceOption,
  SubscriptionServiceDetail,
} from "@/states/service-form/types";
import { formatNumber } from "@/utils/number";
import { MenuSubscriptionViewItems } from "./service-menu-items/MenuSubscriptionViewItems";

interface OptionsAreaProps {
  options?: ServiceOption[];
  onFieldValueChange?: FieldValueChangeHandler;
  isForm?: boolean;
  optionIdsSelected?: number[];
}
/**
 * オプションエリア
 */
const OptionsArea = ({
  options,
  optionIdsSelected,
  onFieldValueChange,
  isForm = true,
}: OptionsAreaProps) => {
  const [optionIds, setOptionIds] = useState<number[]>(optionIdsSelected || []);
  useEffect(() => {
    setOptionIds(optionIdsSelected || []);
  }, [optionIdsSelected]);
  const optionItems = useMemo(() => {
    if (!options || options.length === 0) {
      return [];
    }
    return options.map((option) => {
      return {
        id: `option-${option.id}`,
        label: (
          <h5 className="font-bold break-word leading-5">{option.name}</h5>
        ),
        value: option.id,
        option,
      };
    });
  }, [options]);

  if (optionItems.length === 0) return <></>;

  return (
    <>
      <Label
        isRequireLabel={false}
        disabled={false}
        className="text-gray-600 mt-6 text-base"
      >
        オプション <BudgeOptional />
      </Label>
      <ul className="space-y-4">
        <CheckboxGroup
          items={optionItems}
          groupName="optionIds"
          values={optionIds || []}
          onChange={
            onFieldValueChange
              ? (values) => {
                  setOptionIds(values);
                  return onFieldValueChange("optionIds", values);
                }
              : undefined
          }
          itemRenderer={(children, item) => (
            <li
              className="p-3 mt-4 bg-white border rounded border-[#DFDFE2] md:p-3"
              key={item.option.id}
            >
              <div className="justify-between pb-4 space-x-1 break-all border-b border-theme-light">
                {isForm ? (
                  <div className="max-w-[100%] break-all">{children}</div>
                ) : (
                  <div className="font-bold max-w-[100%] break-all">
                    {item.option.name}
                  </div>
                )}
                <div className="my-auto min-w-[2.5rem] text-right font-bold text-base">
                  {item.option.price ? (
                    `${formatNumber(item.option.price)}円（税込）`
                  ) : item.option.isShowFree ? (
                    <span className="ml-2">無料</span>
                  ) : null}
                </div>
              </div>
              <div className="pt-4 space-y-4">
                {item.option.describe && (
                  <ServiceOptionDescribe describe={item.option.describe} />
                )}
              </div>
            </li>
          )}
          containerClassName="!items-start"
        />
      </ul>
    </>
  );
};
const ServiceMenuContent = ({
  part,
  isConfirm,
}: {
  part: IServiceMenuTemplate;
  isConfirm?: boolean;
}) => {
  const serviceFormUiState = useServiceFormUiState();
  const formData = useFormData();
  const { errors, errorsDraft } = useSubmitForm();
  const service = useServiceInfo();

  const onFieldValueChange = useHandleFieldValueChange();
  const optionIdsSelected =
    serviceFormUiState.status === "Loaded"
      ? isConfirm
        ? serviceFormUiState?.draft?.inputs?.service.optionIds
        : serviceFormUiState?.inputs?.service.optionIds
      : [];
  const containerClass = isConfirm
    ? ""
    : "mx-[-16px] px-[16px] md:mx-[calc((-100vw+920px)/2)] md:px-[calc((100vw-920px)/2)]";

  return (
    <div
      className={containerClass}
      style={isConfirm ? {} : part?.bgCssProperties}
      id="menu"
    >
      {(errors?.service?.detailIds?.[0] || errors?.service?.optionIds?.[0]) &&
      formData.isSubmit === true ? (
        <p className="text-xl text-center text-accent form-error-text">
          {errors?.service?.detailIds?.[0] || errors?.service?.optionIds?.[0]}
        </p>
      ) : null}
      {(errorsDraft?.service?.detailIds?.[0] ||
        errorsDraft?.service?.optionIds?.[0]) &&
      isConfirm &&
      formData.isSubmit === true ? (
        <p className="text-xl text-center text-accent form-error-text">
          {errorsDraft?.service?.detailIds?.[0] ||
            errorsDraft?.service?.optionIds?.[0]}
        </p>
      ) : null}
      {serviceFormUiState.status === "Loaded" && (
        <EditorFrame noBorder isNoPadding>
          <div
            id="service-menu-template"
            className={clsx(
              "py-6 px-3 rounded-lg md:py-12",
              !isConfirm && "md:px-[150px]",
            )}
            style={part.frameCssProperties}
          >
            <div className="text-base font-bold text-gray-600">サービス名</div>
            <div className="text-base font-bold text-gray-900 mt-2 break-word">
              {service?.name}
            </div>
            {part.type === "menu.eventSeminar" ? (
              <>
                <Label
                  isRequireLabel={true}
                  disabled={false}
                  className="text-gray-600 mt-6"
                >
                  <span className="text-base text-[#626262]">開催詳細</span>
                </Label>
                {service?.type === "event-seminar" &&
                  service.capacity_status === CapacityStatus.ALL && (
                    <p className="mb-2">
                      {service &&
                      "view_seats_left" in service &&
                      !!service.view_seats_left
                        ? `残り${service.remaining_capacity}名/`
                        : ""}
                      定員{formatNumber(service.maximum_capacity ?? 0)}名
                    </p>
                  )}
              </>
            ) : (
              <div className="mt-6"></div>
            )}
            {service?.type === "subscription" ? (
              <MenuSubscriptionViewItems
                menus={
                  service?.details as unknown as SubscriptionServiceDetail[]
                }
                reserveStatus={
                  service?.reserveStatus || ServiceReserveStatus.SINGLE_SELECT
                }
                service={service}
                type={part.type}
                isConfirm={isConfirm}
                isViewSeatsLeft={
                  service && "view_seats_left" in service
                    ? !!service.view_seats_left
                    : false
                }
              />
            ) : (
              <>
                <ScheduleViewItems
                  schedules={service?.details as unknown as Schedule[]}
                  reserveStatus={
                    service?.reserveStatus !== undefined
                      ? service?.reserveStatus
                      : ServiceReserveStatus.ALL
                  }
                  service={service}
                  type={part.type}
                  isConfirm={isConfirm}
                  isViewSeatsLeft={
                    service && "view_seats_left" in service
                      ? !!service.view_seats_left
                      : false
                  }
                  disablePast={
                    service && "disable_past_flag" in service
                      ? !!service.disable_past_flag
                      : false
                  }
                />
                <OptionsArea
                  isForm={true}
                  options={service?.options}
                  onFieldValueChange={
                    onFieldValueChange as FieldValueChangeHandler
                  }
                  optionIdsSelected={optionIdsSelected}
                ></OptionsArea>
              </>
            )}
          </div>
        </EditorFrame>
      )}
    </div>
  );
};

export const ServiceMenuTemplate = memo(ServiceMenuContent);
